import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { CheckCircle, XCircle, Hourglass, Search, Home, MapPin, DollarSign, Trash2, Bed, Bath, Square } from 'lucide-react';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Breadcrumb from '../../components/Breadcrumb';

interface Propiedad {
  id: string;
  titulo?: string;
  nombre?: string;
  list_number?: string;
  tipo: string;
  estado: string;
  precio: number;
  flex_url?: string;
  estatus_publicacion: 'pendiente' | 'aprobada' | 'rechazada';
  direccion?: string;
  ciudad?: string;
  dormitorios?: number;
  banos?: number;
  area_construida?: number;
  descripcion?: string;
  inmobiliaria_doc_id?: string;
  fecha_creacion?: { seconds: number };
  [key: string]: any;
}

interface Inmobiliaria {
  id: string;
  nombre: string;
}

export default function AdminAprobarPropiedades() {
  const { user } = useUser();
  const navigate = useNavigate();

  const [allProps, setAllProps] = useState<Propiedad[]>([]);
  const [tabActivo, setTabActivo] = useState<'pendiente' | 'aprobada' | 'rechazada'>('pendiente');
  const [loading, setLoading] = useState(true);
  const [inmobiliarias, setInmobiliarias] = useState<Inmobiliaria[]>([]);
  const [busqueda, setBusqueda] = useState('');
  const [procesando, setProcesando] = useState<string | null>(null);

  // Paginación
  const [paginaActual, setPaginaActual] = useState(1);
  const propiedadesPorPagina = 10;

  // Estadísticas
  const [stats, setStats] = useState({
    total: 0,
    pendientes: 0,
    aprobadas: 0,
    rechazadas: 0,
    precioPromedio: 0
  });

  const cargarInmobiliarias = async () => {
    try {
      const snap = await getDocs(collection(db, 'inmobiliaria'));
      const inmoList: Inmobiliaria[] = snap.docs.map(docSnap => ({
        id: docSnap.id,
        nombre: docSnap.data().nombre || 'Sin nombre'
      }));
      setInmobiliarias(inmoList);
      console.log(`✅ Inmobiliarias cargadas: ${inmoList.length}`);
    } catch (error) {
      console.error('❌ Error cargando inmobiliarias:', error);
    }
  };

  useEffect(() => {
    const cargarDatos = async () => {
      // Validar acceso de administrador
      if (!user || !user.tipo?.includes('admin')) {
        Swal.fire({
          icon: 'error',
          title: 'Acceso denegado',
          text: 'No tienes permiso para acceder a esta sección.'
        });
        navigate('/admin-dashboard');
        return;
      }

      try {
        setLoading(true);

        // Cargar propiedades
        const snap = await getDocs(collection(db, 'propiedades'));
        const props: Propiedad[] = snap.docs.map(docSnap => ({
          id: docSnap.id,
          ...docSnap.data()
        } as Propiedad));

        setAllProps(props);

        // Calcular estadísticas
        const pendientes = props.filter(p => p.estatus_publicacion === 'pendiente').length;
        const aprobadas = props.filter(p => p.estatus_publicacion === 'aprobada').length;
        const rechazadas = props.filter(p => p.estatus_publicacion === 'rechazada').length;

        const propiedadesConPrecio = props.filter(p => p.precio && p.precio > 0);
        const precioPromedio = propiedadesConPrecio.length > 0
          ? Math.round(propiedadesConPrecio.reduce((acc, p) => acc + (p.precio || 0), 0) / propiedadesConPrecio.length)
          : 0;

        setStats({
          total: props.length,
          pendientes,
          aprobadas,
          rechazadas,
          precioPromedio
        });

        // Cargar inmobiliarias
        await cargarInmobiliarias();

        console.log(`✅ Propiedades cargadas: ${props.length} total`);
      } catch (error) {
        console.error('❌ Error cargando propiedades:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Ocurrió un error al cargar las propiedades.'
        });
      } finally {
        setLoading(false);
      }
    };

    cargarDatos();
  }, [user, navigate]);

  // Reiniciar página actual cuando cambia el tab o la búsqueda
  useEffect(() => {
    setPaginaActual(1);
  }, [tabActivo, busqueda]);

  const actualizarEstatus = async (id: string, nuevo: 'aprobada' | 'rechazada') => {
    setProcesando(id);

    try {
      const ref = doc(db, 'propiedades', id);
      await updateDoc(ref, { estatus_publicacion: nuevo });

      // Actualizar estado local
      setAllProps(prev =>
        prev.map(p => (p.id === id ? { ...p, estatus_publicacion: nuevo } : p))
      );

      // Actualizar estadísticas
      setStats(prev => {
        const propAnterior = allProps.find(p => p.id === id);
        if (!propAnterior) return prev;

        const cambios = {
          pendientes: prev.pendientes - (propAnterior.estatus_publicacion === 'pendiente' ? 1 : 0),
          aprobadas: prev.aprobadas + (nuevo === 'aprobada' ? 1 : 0),
          rechazadas: prev.rechazadas + (nuevo === 'rechazada' ? 1 : 0)
        };

        return { ...prev, ...cambios };
      });

      Swal.fire({
        title: '¡Éxito!',
        text: `La propiedad fue ${nuevo === 'aprobada' ? 'aprobada y publicada' : 'rechazada'} correctamente.`,
        icon: 'success',
        confirmButtonText: 'Aceptar',
        timer: 2000
      });

    } catch (error: any) {
      console.error('❌ Error al actualizar propiedad:', error);
      Swal.fire({
        title: 'Error',
        text: error.message || 'Hubo un problema al actualizar la propiedad.',
        icon: 'error',
      });
    } finally {
      setProcesando(null);
    }
  };

  const eliminarPropiedad = async (id: string) => {
    const propiedad = allProps.find(p => p.id === id);
    if (!propiedad) return;

    // Confirmar eliminación con SweetAlert2
    const result = await Swal.fire({
      title: '¿Eliminar propiedad?',
      html: `
        <p>¿Estás seguro de que deseas eliminar esta propiedad?</p>
        <div class="text-start mt-3">
          <strong>Título:</strong> ${propiedad.titulo || propiedad.nombre || 'Sin título'}<br>
          <strong>Tipo:</strong> ${propiedad.tipo}<br>
          <strong>Precio:</strong> $${propiedad.precio?.toLocaleString()}<br>
          <strong>Estado:</strong> <span class="badge bg-${propiedad.estatus_publicacion === 'pendiente' ? 'warning' : propiedad.estatus_publicacion === 'aprobada' ? 'success' : 'danger'}">${propiedad.estatus_publicacion}</span>
        </div>
        <p class="text-danger mt-3 mb-0"><strong>⚠️ Esta acción no se puede deshacer.</strong></p>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) return;

    setProcesando(id);

    try {
      // Eliminar la propiedad de Firestore
      await deleteDoc(doc(db, 'propiedades', id));

      // Actualizar estado local
      const nuevasPropiedades = allProps.filter(p => p.id !== id);
      setAllProps(nuevasPropiedades);

      // Recalcular estadísticas
      const pendientes = nuevasPropiedades.filter(p => p.estatus_publicacion === 'pendiente').length;
      const aprobadas = nuevasPropiedades.filter(p => p.estatus_publicacion === 'aprobada').length;
      const rechazadas = nuevasPropiedades.filter(p => p.estatus_publicacion === 'rechazada').length;

      const propiedadesConPrecio = nuevasPropiedades.filter(p => p.precio && p.precio > 0);
      const precioPromedio = propiedadesConPrecio.length > 0
        ? Math.round(propiedadesConPrecio.reduce((acc, p) => acc + (p.precio || 0), 0) / propiedadesConPrecio.length)
        : 0;

      setStats({
        total: nuevasPropiedades.length,
        pendientes,
        aprobadas,
        rechazadas,
        precioPromedio
      });

      Swal.fire({
        title: '¡Eliminada!',
        text: 'La propiedad ha sido eliminada correctamente.',
        icon: 'success',
        confirmButtonText: 'Aceptar',
        timer: 2000
      });

    } catch (error: any) {
      console.error('❌ Error al eliminar propiedad:', error);
      Swal.fire({
        title: 'Error',
        text: error.message || 'Hubo un problema al eliminar la propiedad.',
        icon: 'error',
      });
    } finally {
      setProcesando(null);
    }
  };

  // Filtrar propiedades por tab y búsqueda
  const propiedadesFiltradas = allProps
    .filter(p => p.estatus_publicacion === tabActivo)
    .filter(p => {
      if (!busqueda.trim()) return true;
      const searchLower = busqueda.toLowerCase();
      const inmobiliaria = inmobiliarias.find(i => i.id === p.inmobiliaria_doc_id);
      return (
        (p.titulo && p.titulo.toLowerCase().includes(searchLower)) ||
        (p.nombre && p.nombre.toLowerCase().includes(searchLower)) ||
        (p.list_number && p.list_number.toLowerCase().includes(searchLower)) ||
        (p.tipo && p.tipo.toLowerCase().includes(searchLower)) ||
        (p.ciudad && p.ciudad.toLowerCase().includes(searchLower)) ||
        (p.direccion && p.direccion.toLowerCase().includes(searchLower)) ||
        (inmobiliaria && inmobiliaria.nombre.toLowerCase().includes(searchLower))
      );
    });

  // Paginación
  const totalPaginas = Math.ceil(propiedadesFiltradas.length / propiedadesPorPagina);
  const indiceInicio = (paginaActual - 1) * propiedadesPorPagina;
  const indiceFin = indiceInicio + propiedadesPorPagina;
  const propiedadesPaginadas = propiedadesFiltradas.slice(indiceInicio, indiceFin);

  const colores = {
    pendiente: { bg: 'warning', border: 'warning', text: 'warning' },
    aprobada: { bg: 'success', border: 'success', text: 'success' },
    rechazada: { bg: 'danger', border: 'danger', text: 'danger' },
  };

  const iconos = {
    pendiente: <Hourglass size={18} />,
    aprobada: <CheckCircle size={18} />,
    rechazada: <XCircle size={18} />,
  };

  if (loading) {
    return (
      <>
        <UserNav />
        <div className="container py-5 text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-3 text-muted">Cargando propiedades...</p>
        </div>
      </>
    );
  }

  if (!user || !user.tipo?.includes('admin')) {
    return (
      <>
        <UserNav />
        <div className="container mt-5">
          <div className="alert alert-danger">
            <h4>Acceso Denegado</h4>
            <p>No tienes permiso para acceder a esta sección</p>
            <button className="btn btn-primary" onClick={() => navigate('/admin-dashboard')}>
              Volver al Dashboard
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <UserNav />

      <div className="page-title">
        <div className="container">
          <h2 className="ipt-title">Aprobación de Propiedades</h2>
          <span className="ipn-subtitle">Gestión y moderación de propiedades</span>
        </div>
      </div>

      <section className="gray-simple">
        <div className="container">
          <Breadcrumb
            items={[
              { label: 'Dashboard', path: '/admin-dashboard' },
              { label: 'Aprobación de Propiedades' }
            ]}
          />

          {/* Tarjetas de Estadísticas */}
          <div className="row g-3 mb-4">
            <div className="col-md-3 col-sm-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="bg-primary bg-opacity-10 p-3 rounded">
                        <Home className="text-primary" size={24} />
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <h6 className="text-muted mb-0">Total Propiedades</h6>
                      <h3 className="mb-0">{stats.total}</h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-3 col-sm-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="bg-warning bg-opacity-10 p-3 rounded">
                        <Hourglass className="text-warning" size={24} />
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <h6 className="text-muted mb-0">Pendientes</h6>
                      <h3 className="mb-0">{stats.pendientes}</h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-3 col-sm-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="bg-success bg-opacity-10 p-3 rounded">
                        <CheckCircle className="text-success" size={24} />
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <h6 className="text-muted mb-0">Aprobadas</h6>
                      <h3 className="mb-0">{stats.aprobadas}</h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-3 col-sm-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="bg-info bg-opacity-10 p-3 rounded">
                        <DollarSign className="text-info" size={24} />
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <h6 className="text-muted mb-0">Precio Promedio</h6>
                      <h3 className="mb-0">${(stats.precioPromedio / 1000).toFixed(0)}K</h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filtros y Búsqueda */}
          <div className="card mb-4 border-0 shadow-sm">
            <div className="card-body">
              <div className="row g-3 align-items-center">
                <div className="col-md-6">
                  <div className="btn-group w-100" role="group">
                    {(['pendiente', 'aprobada', 'rechazada'] as const).map((estado) => (
                      <button
                        key={estado}
                        className={`btn ${tabActivo === estado ? `btn-${colores[estado].bg}` : `btn-outline-${colores[estado].border}`}`}
                        onClick={() => setTabActivo(estado)}
                      >
                        <span className="d-inline-flex align-items-center gap-2">
                          {iconos[estado]}
                          <span>{estado.charAt(0).toUpperCase() + estado.slice(1)}</span>
                          <span className="badge bg-white text-dark">{
                            estado === 'pendiente' ? stats.pendientes :
                            estado === 'aprobada' ? stats.aprobadas :
                            stats.rechazadas
                          }</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="input-group">
                    <span className="input-group-text bg-white">
                      <Search size={18} />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Buscar por título, tipo, ciudad o inmobiliaria..."
                      value={busqueda}
                      onChange={(e) => setBusqueda(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Lista de Propiedades */}
          {propiedadesFiltradas.length === 0 ? (
            <div className="card border-0 shadow-sm">
              <div className="card-body text-center py-5">
                <Home size={48} className="text-muted mb-3" />
                <h5 className="text-muted">No hay propiedades {tabActivo}s</h5>
                <p className="text-muted mb-0">
                  {busqueda ? 'Intenta con otros términos de búsqueda' : 'Las propiedades aparecerán aquí'}
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="row g-3">
                {propiedadesPaginadas.map((prop) => {
                  const inmobiliaria = inmobiliarias.find(i => i.id === prop.inmobiliaria_doc_id);
                  return (
                    <div key={prop.id} className="col-12">
                      <div className={`card border-start border-${colores[tabActivo].border} border-4 shadow-sm h-100`}>
                        <div className="card-body">
                          <div className="row">
                            {/* Columna Principal */}
                            <div className="col-lg-8">
                              <div className="d-flex align-items-start mb-3">
                                <div className={`badge bg-${colores[tabActivo].bg} me-2`}>
                                  {tabActivo.toUpperCase()}
                                </div>
                                <div className="flex-grow-1">
                                  <h5 className="mb-1">
                                    <Home size={18} className="me-2" />
                                    {prop.titulo || prop.nombre || `Propiedad ${prop.list_number || 'Sin título'}`}
                                  </h5>
                                  {prop.list_number && (
                                    <span className="badge bg-secondary">ID: {prop.list_number}</span>
                                  )}
                                </div>
                              </div>

                              <div className="mb-3">
                                <strong className="text-muted d-block mb-1">Información:</strong>
                                <div className="d-flex flex-wrap gap-3">
                                  <span><strong>Tipo:</strong> {prop.tipo}</span>
                                  <span><strong>Estado:</strong> {prop.estado}</span>
                                  {inmobiliaria && (
                                    <span><strong>Inmobiliaria:</strong> {inmobiliaria.nombre}</span>
                                  )}
                                </div>
                              </div>

                              {(prop.direccion || prop.ciudad) && (
                                <div className="mb-3">
                                  <strong className="text-muted d-block mb-1">
                                    <MapPin size={14} className="me-1" />
                                    Ubicación:
                                  </strong>
                                  <p className="mb-0">
                                    {prop.direccion && `${prop.direccion}${prop.ciudad ? ', ' : ''}`}
                                    {prop.ciudad}
                                  </p>
                                </div>
                              )}

                              {prop.descripcion && (
                                <div className="mb-3">
                                  <strong className="text-muted d-block mb-1">Descripción:</strong>
                                  <p className="mb-0 text-break">{prop.descripcion.substring(0, 200)}{prop.descripcion.length > 200 ? '...' : ''}</p>
                                </div>
                              )}

                              {prop.flex_url && (
                                <div className="mb-3">
                                  <a
                                    href={prop.flex_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-outline-primary"
                                  >
                                    Ver en Flexmls
                                  </a>
                                </div>
                              )}
                            </div>

                            {/* Columna de Detalles y Acciones */}
                            <div className="col-lg-4">
                              <div className="border-start ps-3 h-100 d-flex flex-column">
                                <div className="mb-3">
                                  <strong className="text-muted d-block mb-2">
                                    <DollarSign size={14} className="me-1" />
                                    Precio:
                                  </strong>
                                  <h4 className="text-success mb-0">
                                    ${prop.precio?.toLocaleString()}
                                  </h4>
                                </div>

                                {/* Características */}
                                {(prop.dormitorios || prop.banos || prop.area_construida) && (
                                  <div className="mb-3">
                                    <strong className="text-muted d-block mb-2">Características:</strong>
                                    <div className="d-flex flex-wrap gap-2">
                                      {prop.dormitorios && (
                                        <span className="badge bg-light text-dark">
                                          <Bed size={14} className="me-1" />
                                          {prop.dormitorios} Dorm
                                        </span>
                                      )}
                                      {prop.banos && (
                                        <span className="badge bg-light text-dark">
                                          <Bath size={14} className="me-1" />
                                          {prop.banos} Baños
                                        </span>
                                      )}
                                      {prop.area_construida && (
                                        <span className="badge bg-light text-dark">
                                          <Square size={14} className="me-1" />
                                          {prop.area_construida} m²
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {prop.fecha_creacion && (
                                  <div className="mb-3">
                                    <strong className="text-muted d-block mb-1">Creada:</strong>
                                    <small>
                                      {new Date(prop.fecha_creacion.seconds * 1000).toLocaleDateString('es-MX', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric'
                                      })}
                                    </small>
                                  </div>
                                )}

                                {/* Botones de Acción */}
                                <div className="mt-auto">
                                  <div className="d-grid gap-2">
                                    {tabActivo === 'pendiente' && (
                                      <>
                                        <button
                                          className="btn btn-success"
                                          onClick={() => actualizarEstatus(prop.id, 'aprobada')}
                                          disabled={procesando === prop.id}
                                        >
                                          {procesando === prop.id ? (
                                            <>
                                              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                              Procesando...
                                            </>
                                          ) : (
                                            <>
                                              <CheckCircle size={16} className="me-2" />
                                              Aprobar
                                            </>
                                          )}
                                        </button>
                                        <button
                                          className="btn btn-warning"
                                          onClick={() => actualizarEstatus(prop.id, 'rechazada')}
                                          disabled={procesando === prop.id}
                                        >
                                          <XCircle size={16} className="me-2" />
                                          Rechazar
                                        </button>
                                      </>
                                    )}
                                    <button
                                      className="btn btn-outline-danger"
                                      onClick={() => eliminarPropiedad(prop.id)}
                                      disabled={procesando === prop.id}
                                    >
                                      <Trash2 size={16} className="me-2" />
                                      Eliminar
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Controles de Paginación */}
              {totalPaginas > 1 && (
                <div className="card border-0 shadow-sm mt-4">
                  <div className="card-body">
                    <nav>
                      <ul className="pagination justify-content-center mb-0">
                        <li className={`page-item ${paginaActual === 1 ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => setPaginaActual(prev => Math.max(1, prev - 1))}
                            disabled={paginaActual === 1}
                          >
                            Anterior
                          </button>
                        </li>

                        {Array.from({ length: totalPaginas }, (_, i) => i + 1).map(numeroPagina => {
                          // Mostrar solo algunas páginas alrededor de la actual
                          if (
                            numeroPagina === 1 ||
                            numeroPagina === totalPaginas ||
                            (numeroPagina >= paginaActual - 2 && numeroPagina <= paginaActual + 2)
                          ) {
                            return (
                              <li
                                key={numeroPagina}
                                className={`page-item ${paginaActual === numeroPagina ? 'active' : ''}`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() => setPaginaActual(numeroPagina)}
                                >
                                  {numeroPagina}
                                </button>
                              </li>
                            );
                          } else if (
                            numeroPagina === paginaActual - 3 ||
                            numeroPagina === paginaActual + 3
                          ) {
                            return (
                              <li key={numeroPagina} className="page-item disabled">
                                <span className="page-link">...</span>
                              </li>
                            );
                          }
                          return null;
                        })}

                        <li className={`page-item ${paginaActual === totalPaginas ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => setPaginaActual(prev => Math.min(totalPaginas, prev + 1))}
                            disabled={paginaActual === totalPaginas}
                          >
                            Siguiente
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Información adicional */}
          {propiedadesFiltradas.length > 0 && (
            <div className="card border-0 shadow-sm mt-4">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <small className="text-muted">
                    Mostrando {indiceInicio + 1} - {Math.min(indiceFin, propiedadesFiltradas.length)} de {propiedadesFiltradas.length} propiedades filtradas ({stats.total} totales)
                  </small>
                  <small className="text-muted">
                    Precio promedio: <strong>${stats.precioPromedio.toLocaleString()}</strong>
                  </small>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
